import { useState, useRef } from 'react';
import { Users, Plus, Minus, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import connieBreakfast from '../../../assets/connie-french-breakfast-no-bckgnd.png';
import type { FormData } from '../../../types';
import { CtaButton } from './shared';
import { useClickOutside } from '../useClickOutside';

interface Props { form: FormData; onChange: (f: keyof FormData, v: unknown) => void; onNext: () => void; onBack?: () => void; }

function Counter({ label, sub, value, min, onChange }: { label: string; sub: string; value: number; min: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${theme.spacing.xl} 0` }}>
      <div>
        <div style={{ fontWeight: theme.fontWeight.bold, color: theme.colors.navy }}>{label}</div>
        <div style={{ fontSize: theme.fontSize.xs, color: theme.colors.muted }}>{sub}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
        <button type="button" onClick={() => value > min && onChange(value - 1)} disabled={value <= min}
          style={{ width: 40, height: 40, borderRadius: '50%', border: `1px solid ${theme.colors.border}`, background: 'white', cursor: value <= min ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: value <= min ? 0.3 : 1, transition: 'background 0.15s' }}>
          <Minus size={14} />
        </button>
        <span style={{ fontFamily: theme.fontFamily.display, fontSize: theme.fontSize['2xl'], fontWeight: theme.fontWeight.bold, width: 32, textAlign: 'center', color: theme.colors.navy }}>{value}</span>
        <button type="button" onClick={() => onChange(value + 1)}
          style={{ width: 40, height: 40, borderRadius: '50%', border: `1px solid ${theme.colors.border}`, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}>
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

export function StepTravelers({ form, onChange, onNext, onBack }: Props) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setIsOpen(false));

  const adults = Number(form.adults) || 1;
  const children = Number(form.children) || 0;

  return (
    <div style={{ maxWidth: mobile ? '100%' : 'min(80%, 700px)', margin: '0 auto' }}>
      {/* Connie heading */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md, marginBottom: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
        <img src={connieBreakfast} alt="Connie" referrerPolicy="no-referrer"
          style={{ width: 'auto', height: mobile ? 'min(12vh, 100px)' : 'min(20vh, 250px)', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: f.headingSize, fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0, letterSpacing: '-0.02em', textAlign: 'center' }}>
            "Who will{' '}
            <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>join us?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: f.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xs} 0 0`, textAlign: 'center' }}>
            The best memories are shared ones.
          </p>
        </div>
      </div>

      {/* Dropdown selector */}
      <div ref={containerRef} style={{ position: 'relative', maxWidth: mobile ? '100%' : 'min(70%, 480px)', margin: '0 auto', fontFamily: theme.fontFamily.base }}>
        <button type="button" onClick={() => setIsOpen(o => !o)} style={{
          width: '100%', background: 'white', border: `2px solid ${isOpen ? theme.colors.teal : theme.colors.borderLight}`,
          borderRadius: theme.borderRadius['2xl'], padding: theme.spacing.xl,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', transition: 'border-color 0.15s', boxShadow: theme.shadows.card,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
            <div style={{ width: 48, height: 48, background: theme.colors.tealBg, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={24} color={theme.colors.teal} />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.12em', color: theme.colors.muted }}>Travelers</div>
              <div style={{ fontSize: theme.fontSize.lg, fontWeight: theme.fontWeight.bold, color: theme.colors.navy }}>
                {adults} Adult{adults !== 1 ? 's' : ''}, {children} Child{children !== 1 ? 'ren' : ''}
              </div>
            </div>
          </div>
          <ChevronDown size={24} color={theme.colors.muted} style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 50,
                background: 'white', borderRadius: theme.borderRadius['2xl'],
                border: `1px solid ${theme.colors.borderLight}`,
                boxShadow: theme.shadows.xl, padding: `0 ${theme.spacing['2xl']}`,
              }}
            >
              <Counter label="Adults" sub="Ages 13 or above" value={adults} min={1} onChange={v => onChange('adults', v)} />
              <div style={{ borderTop: `1px solid ${theme.colors.borderLight}` }} />
              <Counter label="Children" sub="Ages 2–12" value={children} min={0} onChange={v => onChange('children', v)} />
              <div style={{ padding: `${theme.spacing.lg} 0` }}>
                <button type="button" onClick={() => setIsOpen(false)} style={{
                  width: '100%', padding: theme.spacing.lg, background: theme.colors.navy, color: 'white',
                  border: 'none', borderRadius: theme.borderRadius.full, fontWeight: theme.fontWeight.bold,
                  fontSize: theme.fontSize.base, cursor: 'pointer', letterSpacing: '0.05em',
                }}>
                  Done
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <CtaButton label="Continue" onClick={onNext} disabled={adults < 1} onBack={onBack} />
    </div>
  );
}
